import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SmartphoneIcon, WatchIcon, HeadphonesIcon, BatteryChargingIcon, ZapIcon, Gamepad2Icon, MonitorIcon, ChevronRightIcon, LayoutGridIcon } from 'lucide-react';
import { categories } from '../../data/categories';
import { Button } from '../ui/Button';
const iconMap: Record<string, React.ReactNode> = {
  Smartphone: <SmartphoneIcon className="w-5 h-5" data-id="element-97" data-mp="3v63kbrfo54rf" />,
  Watch: <WatchIcon className="w-5 h-5" data-id="element-98" data-mp="3v5aauipbs15l" />,
  Headphones: <HeadphonesIcon className="w-5 h-5" data-id="element-99" data-mp="3rea02de5gx2" />,
  Battery: <BatteryChargingIcon className="w-5 h-5" data-id="element-100" data-mp="3s7lthr7iwm2" />,
  Zap: <ZapIcon className="w-5 h-5" data-id="element-101" data-mp="3m5dtl259m4kk" />,
  Gamepad2: <Gamepad2Icon className="w-5 h-5" data-id="element-102" data-mp="3m6gl9caewb1j" />,
  Monitor: <MonitorIcon className="w-5 h-5" data-id="element-103" data-mp="3m3kmjlqqpic1" />
};
export function CategorySection() {
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const activeCategory = categories.find(c => c.id === hoveredCategory);
  return <>
      {/* Desktop Sidebar (lg and up) */}
      <div className="hidden lg:block relative w-[280px] flex-shrink-0 z-40" onMouseLeave={() => setHoveredCategory(null)} data-id="element-104" data-mp="3h8exie21jnjq">
        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden h-full flex flex-col" data-id="element-105" data-mp="3aybh5vqelpmi">
          {/* Header */}
          <div className="bg-gray-900 text-white py-3 px-4 flex items-center gap-3 font-semibold" data-id="element-106" data-mp="3owjazus58atg">
            <LayoutGridIcon className="w-5 h-5" data-id="element-107" data-mp="3kxk62a6ewe20" />
            <span data-id="element-108" data-mp="2x6oad1yu4yph">All Categories</span>
          </div>

          {/* Category List */}
          <div className="flex-1 py-2 relative" data-id="element-109" data-mp="2x4adcnklhcfa">
            {categories.map(category => <div key={category.id} onMouseEnter={() => setHoveredCategory(category.id)} className="group" data-id="element-110" data-mp="2s9emvrv0ax4a">
                <Link to={`/products?category=${category.slug}`} className={`flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0 transition-colors ${hoveredCategory === category.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-111" data-mp="3612yzv599ll9">
                  <div className="flex items-center gap-3" data-id="element-112" data-mp="29gkg3l7jebb5">
                    <span className={`${hoveredCategory === category.id ? 'text-blue-600' : 'text-gray-500 group-hover:text-blue-600'} transition-colors`} data-id="element-113" data-mp="cgzi34vmi7zb">
                      {iconMap[category.icon]}
                    </span>
                    <span className="text-sm font-medium" data-id="element-114" data-mp="m7kfiozk96gn">{category.name}</span>
                  </div>
                  <ChevronRightIcon className={`w-4 h-4 transition-all ${hoveredCategory === category.id ? 'opacity-100 text-blue-600 translate-x-0' : 'opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-gray-400'}`} data-id="element-115" data-mp="r4sxaogsr8hd" />
                </Link>
              </div>)}
          </div>
        </div>

        {/* Flyout Panel */}
        {activeCategory && <div className="absolute top-0 left-full ml-2 w-[400px] bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-left-2 duration-200" data-id="element-116" data-mp="8fw1gg5r47x1">
            <div className="p-6" data-id="element-117" data-mp="1jt89724mtaoh">
              <div className="flex items-start justify-between mb-4" data-id="element-118" data-mp="1js6mg0e96zqx">
                <div data-id="element-119" data-mp="34b0nxatb9g76">
                  <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-120" data-mp="34biogjn0agkw">
                    {activeCategory.name}
                  </h3>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-121" data-mp="2um8ypafmx9zy">
                    {activeCategory.productCount} Products
                  </span>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-blue-600" data-id="element-122" data-mp="2kp4e3f1ftml9">
                  {iconMap[activeCategory.icon]}
                </div>
              </div>

              <p className="text-gray-600 text-sm mb-6" data-id="element-123" data-mp="2bo66kmrsiwxx">
                {activeCategory.description}
              </p>

              <div className="relative h-48 rounded-lg overflow-hidden mb-6 group" data-id="element-124" data-mp="26q4l5v6w6lja">
                <img src={activeCategory.image} alt={activeCategory.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-125" data-mp="9vhy9z0ahjba" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-126" data-mp="2yon6zkvswlbe" />
              </div>

              <Link to={`/products?category=${activeCategory.slug}`} data-id="element-127" data-mp="32p73qncgpmx0">
                <Button variant="primary" className="w-full" data-id="element-128" data-mp="2or8q5k5ytuik">
                  Browse {activeCategory.name}
                </Button>
              </Link>
            </div>
          </div>}
      </div>

      {/* Mobile Horizontal Scroll (below lg) */}
      <div className="lg:hidden py-4 bg-white border-b border-gray-100" data-id="element-129" data-mp="2ezmjm14kojm5">
        <div className="max-w-7xl mx-auto px-4" data-id="element-130" data-mp="2jsnwy4p4sb3m">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-131" data-mp="2607sr0khu714">
            {categories.map(category => <Link key={category.id} to={`/products?category=${category.slug}`} className="flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-blue-50 hover:text-blue-600 rounded-full text-sm font-medium text-gray-700 transition-colors whitespace-nowrap border border-gray-100 flex-shrink-0" data-id="element-132" data-mp="2b0dn4oi5e7mw">
                <span className="text-gray-500" data-id="element-133" data-mp="1hge6oxshhwde">{iconMap[category.icon]}</span>
                {category.name}
              </Link>)}
          </div>
        </div>
      </div>
    </>;
}